import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "Which statement about budgeting is most accurate?",
    options: [
      "'Budgeting is only for people with financial problems.'",
      "'Budgeting helps everyone manage their money better.'",
      "'Budgeting is a waste of time.'"
    ],
    correctAnswer: 1,
    explanation: "Budgeting is a valuable tool for everyone, not just those with financial problems. It helps you manage your money, set goals, and make informed decisions."
  },
  {
    question: "How does financial stress typically manifest physically?",
    options: [
      "Headaches, sleep problems, and weakened immune system",
      "Improved concentration",
      "Increased energy levels"
    ],
    correctAnswer: 0,
    explanation: "Financial stress often manifests as physical symptoms including headaches, sleep disturbances, digestive issues, and a weakened immune system due to chronic activation of the stress response."
  },
  {
    question: "What is the most effective way to reduce financial anxiety?",
    options: [
      "Creating a financial safety net and emergency fund",
      "Avoiding all financial discussions",
      "Spending more money on entertainment"
    ],
    correctAnswer: 0,
    explanation: "Building a financial safety net and emergency fund provides security and reduces anxiety. Knowing you have resources to handle unexpected expenses significantly lowers financial stress."
  },
  {
    question: "Which cognitive distortion is common with financial stress?",
    options: [
      "All-or-nothing thinking about money situations",
      "Realistic assessment of financial situations",
      "Optimistic outlook on all financial matters"
    ],
    correctAnswer: 0,
    explanation: "All-or-nothing thinking is common in financial stress, where people see situations as completely good or bad, successful or failed, rather than recognizing the nuances and possibilities for improvement."
  },
  {
    question: "How does financial stress affect decision-making?",
    options: [
      "It impairs judgment and leads to impulsive choices",
      "It improves analytical thinking",
      "It has no effect on decision-making"
    ],
    correctAnswer: 0,
    explanation: "Financial stress activates the brain's threat response, impairing the prefrontal cortex function responsible for rational decision-making, often leading to impulsive or emotionally-driven financial choices."
  }
];

const FinancialModule2QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('financialM2');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 2 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                The psychology of financial stress
              </li>
              <li>
                IFDFW assessment framework
              </li>
              <li>
                Stress mapping techniques
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue your financial wellness journey by learning about financial resilience!</p>
            <p>Ready to explore strategies for building long-term financial stability?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/financial-resources')}
          >
            Return to Financial Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left financial-panel">
          <div className="module-header">
            <h1>Module 2 Quiz</h1>
            <p className="module-subtitle">The Psychology of Financial Stress</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about financial stress and its impact.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/financial-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialModule2QuizPage;

